import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Switch,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Heading,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";

const USER_SETTINGS_COLLECTION = "userSettings";
const USER_SETTINGS_DOC_ID = "global";

export const DEFAULT_USER_VERIFICATION_SETTINGS = {
  requireEmailVerificationAdmin: true,
  requireEmailVerificationStaff: false,
  requireEmailVerificationKds: false,
};

const UserSettings: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requireEmailVerificationAdmin, setRequireEmailVerificationAdmin] = useState(
    DEFAULT_USER_VERIFICATION_SETTINGS.requireEmailVerificationAdmin
  );
  const [requireEmailVerificationStaff, setRequireEmailVerificationStaff] = useState(
    DEFAULT_USER_VERIFICATION_SETTINGS.requireEmailVerificationStaff
  );
  const [requireEmailVerificationKds, setRequireEmailVerificationKds] = useState(
    DEFAULT_USER_VERIFICATION_SETTINGS.requireEmailVerificationKds
  );

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "white");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    const load = async () => {
      try {
        const ref = doc(db, USER_SETTINGS_COLLECTION, USER_SETTINGS_DOC_ID);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setRequireEmailVerificationAdmin(data.requireEmailVerificationAdmin !== false);
          setRequireEmailVerificationStaff(data.requireEmailVerificationStaff === true);
          setRequireEmailVerificationKds(data.requireEmailVerificationKds === true);
        }
      } catch (error) {
        console.error("Error loading user settings:", error);
        toast({
          title: "Could not load user settings",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const ref = doc(db, USER_SETTINGS_COLLECTION, USER_SETTINGS_DOC_ID);
      await setDoc(
        ref,
        {
          requireEmailVerificationAdmin,
          requireEmailVerificationStaff,
          requireEmailVerificationKds,
          lastUpdated: serverTimestamp(),
          updatedBy: currentUser?.email || "unknown",
        },
        { merge: true }
      );
      toast({
        title: "User settings saved",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save user settings",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      label: "Admin",
      description: "Requires email verification by default.",
      checked: requireEmailVerificationAdmin,
      onChange: setRequireEmailVerificationAdmin,
    },
    {
      label: "Member",
      description: "No verification required by default. Back-office members only. Not a POS user and cannot generate sales.",
      checked: requireEmailVerificationStaff,
      onChange: setRequireEmailVerificationStaff,
    },
    {
      label: "KDS user",
      description: "No verification required by default. Kitchen display only. Not a POS user and cannot generate sales.",
      checked: requireEmailVerificationKds,
      onChange: setRequireEmailVerificationKds,
    },
  ];

  return (
    <PageShell
      title="User Settings"
      description="Choose which roles must verify their email before they can sign in."
      actions={
        <Button onClick={handleSave} isLoading={saving} isDisabled={loading}>
          Save
        </Button>
      }
    >
      <VStack spacing={6} align="stretch">

        <Alert status="info" borderRadius="xl">
          <AlertIcon />
          <AlertDescription>
            Firebase already allows an account to be created and used without a verified email.
            These switches only control whether this POS asks for that verification.
          </AlertDescription>
        </Alert>

        <Box bg={bgCard} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" p={6}>
          <Text fontWeight="600" fontSize="md" mb={1}>Sign-up email verification</Text>
          <Text fontSize="sm" color={textSecondary} mb={4}>
            Turn a role on to require the verification email. Turn it off to let that role sign in immediately.
          </Text>
          {loading ? (
            <Spinner />
          ) : (
            <VStack align="stretch" spacing={0} divider={<Divider />}>
              {rows.map((row) => (
                <FormControl key={row.label} display="flex" alignItems="center" justifyContent="space-between" py={4}>
                  <Box mr={4}>
                    <FormLabel mb="0" fontWeight="600">{row.label}</FormLabel>
                    <Text fontSize="xs" color={textSecondary}>{row.description}</Text>
                  </Box>
                  <Switch
                    isChecked={row.checked}
                    onChange={(event) => row.onChange(event.target.checked)}
                    colorScheme="blue"
                    size="md"
                  />
                </FormControl>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </PageShell>
  );
};

export default UserSettings;
